/**
 * Centralized API Endpoint Configuration
 *
 * Automatically determines the correct API base URL based on the environment.
 * Supports:
 * - localhost → local development
 * - *.dev.smartr365.com → DEV environment
 * - *.release.smartr365.com → RELEASE environment
 * - * → PRODUCTION environment
 */

const getApiBaseUrl = (): string => {
  // Check for environment variable (injected at build time or runtime)
  if (process.env.REACT_APP_API_BASE_URL) {
    console.log('[Endpoints] Using REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Determine environment from hostname
  const hostname = window.location.hostname;
  console.log('[Endpoints] Detecting environment from hostname:', hostname);

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('[Endpoints] Using LOCAL environment');
    return 'http://localhost:7071';
  }

  if (hostname.includes('dev.smartr365.com') || hostname.includes('white-mud-048ede103')) {
    console.log('[Endpoints] Using DEV environment');
    return 'https://smt-func-onboarding-dev.azurewebsites.net';
  }

  if (hostname.includes('release.smartr365.com') || hostname.includes('mango-dune-0600fbf03')) {
    console.log('[Endpoints] Using RELEASE environment');
    return 'https://smt-func-onboarding-release.azurewebsites.net';
  }

  // Default to production (NEW infrastructure - North Europe)
  console.log('[Endpoints] Using PRODUCTION environment (default)');
  return 'https://smt-func-onboarding-prod-neu.azurewebsites.net';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * All API Endpoints
 * Import this object in your services to get the correct endpoint for the current environment
 */
export const ENDPOINTS = {
  // Business Name Validation
  CHECK_BUSINESS_NAME: `${API_BASE_URL}/api/CheckBusinessNameAvailability`,

  // DNS Validation
  CHECK_DNS_AVAILABILITY: `${API_BASE_URL}/api/CheckDNSAvailability`,

  // FCA Validation
  CHECK_FCA_EXISTENCE: `${API_BASE_URL}/api/CheckFCAExistence`,
  FCA_VALIDATION_WEBHOOK: `${API_BASE_URL}/api/HttpTriggerFCAValidation`,

  // Onboarding (environment-specific triggers)
  TRIGGER_ONBOARDING: `${API_BASE_URL}/api/TriggerOnboarding`,
  TRIGGER_ONBOARDING_DEV: `${API_BASE_URL}/api/TriggerOnboardingDev`,
  TRIGGER_ONBOARDING_RELEASE: `${API_BASE_URL}/api/TriggerOnboardingRelease`,

  // Pipeline Status
  POLL_PIPELINE_STATUS: `${API_BASE_URL}/api/PollPipelineStatus`,

  // Network Onboarding
  NETWORK_ONBOARDING: `${API_BASE_URL}/api/NetworkOnboarding`,
  VALIDATE_NETWORK_PIN: `${API_BASE_URL}/api/ValidateNetworkPIN`,

  // Admin
  ADMIN_ONBOARDING: `${API_BASE_URL}/api/AdminOnboarding`,
};

/**
 * Get the current environment name
 */
export const getEnvironment = (): 'local' | 'dev' | 'release' | 'production' => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }

  if (hostname.includes('dev.smartr365.com') || hostname.includes('white-mud-048ede103')) {
    return 'dev';
  }

  if (hostname.includes('release.smartr365.com') || hostname.includes('mango-dune-0600fbf03')) {
    return 'release';
  }

  return 'production';
};

/**
 * Get the domain suffix for the current environment
 * Returns the appropriate domain suffix:
 * - DEV: .dev.smartr365.com
 * - RELEASE: .release.smartr365.com
 * - PRODUCTION: .app.smartr365.com (only production uses "app." prefix)
 */
export const getAppDomainSuffix = (): string => {
  const env = getEnvironment();

  switch (env) {
    case 'local':
      return '.dev.smartr365.com'; // Use DEV for local development
    case 'dev':
      return '.dev.smartr365.com';
    case 'release':
      return '.release.smartr365.com';
    case 'production':
      return '.app.smartr365.com';
    default:
      return '.app.smartr365.com';
  }
};

/**
 * Get the full app domain for a given subdomain in the current environment
 * @param subdomain - The subdomain (e.g., "testbusiness")
 * @returns Full domain (e.g., "testbusiness.dev.smartr365.com")
 */
export const getFullAppDomain = (subdomain: string): string => {
  return `${subdomain}${getAppDomainSuffix()}`;
};

/**
 * Get the correct onboarding trigger endpoint for the current environment
 * @returns The environment-specific trigger endpoint
 */
export const getTriggerOnboardingEndpoint = (): string => {
  const env = getEnvironment();

  switch (env) {
    case 'local':
    case 'dev':
      return ENDPOINTS.TRIGGER_ONBOARDING_DEV;
    case 'release':
      return ENDPOINTS.TRIGGER_ONBOARDING_RELEASE;
    case 'production':
      return ENDPOINTS.TRIGGER_ONBOARDING;
    default:
      return ENDPOINTS.TRIGGER_ONBOARDING;
  }
};

// Log configuration on load
console.log('[Endpoints] API Base URL:', API_BASE_URL);
console.log('[Endpoints] Environment detected from:', window.location.hostname);
console.log('[Endpoints] Environment:', getEnvironment());
console.log('[Endpoints] App Domain Suffix:', getAppDomainSuffix());
